import { forwardRef } from "react";
import { formatCurrency, formatDateTime } from "../../lib/utils";

// وصل احترافي قابل للطباعة والإرسال عبر واتساب
export const ProfessionalReceipt = forwardRef(({ 
  type = "damage", // damage, salary, installment, cashbox
  data = {},
  shopInfo = {},
  language = "ar"
}, ref) => {
  
  const getTitle = () => {
    switch (type) {
      case "damage": return "وصل تالفات";
      case "salary": return "وصل راتب";
      case "installment": return "وصل دفعة تقسيط";
      case "cashbox": return "تقرير إغلاق الصندوق";
      default: return "وصل";
    }
  };

  const getTypeColor = () => {
    switch (type) {
      case "damage": return "from-red-600 to-red-700";
      case "salary": return "from-blue-600 to-blue-700";
      case "installment": return "from-purple-600 to-purple-700";
      case "cashbox": return "from-emerald-600 to-emerald-700";
      default: return "from-slate-600 to-slate-700";
    }
  };

  return (
    <div 
      ref={ref}
      dir="rtl"
      className="bg-white text-black p-6 max-w-md mx-auto"
      style={{ fontFamily: "Arial, sans-serif" }}
    >
      {/* Header with Shop Name */}
      <div className={`bg-gradient-to-r ${getTypeColor()} text-white p-4 rounded-t-lg -mx-6 -mt-6 mb-4`}>
        <h1 className="text-xl font-bold text-center">{shopInfo.name || "اسم المؤسسة"}</h1>
        {shopInfo.phone && (
          <p className="text-center text-sm opacity-90 mt-1">📞 {shopInfo.phone}</p>
        )}
        {shopInfo.address && (
          <p className="text-center text-xs opacity-80 mt-1">📍 {shopInfo.address}</p>
        )}
      </div>

      {/* Receipt Title */}
      <div className="text-center mb-4">
        <h2 className="text-lg font-bold border-b-2 border-dashed border-gray-300 pb-2">
          {getTitle()}
        </h2>
        <p className="text-xs text-gray-500 mt-1">
          رقم: {data.invoice_number || data.id?.slice(0, 8) || "---"}
        </p>
      </div>

      {/* Date & Time */}
      <div className="flex justify-between text-sm mb-4 text-gray-600">
        <span>التاريخ: {formatDateTime(data.date || data.created_at || new Date()).split(" ")[0]}</span>
        <span>الوقت: {formatDateTime(data.date || data.created_at || new Date()).split(" ")[1]}</span>
      </div>

      {/* Content based on type */}
      {type === "damage" && (
        <DamageContent data={data} />
      )}
      {type === "salary" && (
        <SalaryContent data={data} shopInfo={shopInfo} />
      )}
      {type === "installment" && (
        <InstallmentContent data={data} />
      )}
      {type === "cashbox" && (
        <CashboxContent data={data} />
      )}

      {/* Footer */}
      <div className="border-t-2 border-dashed border-gray-300 mt-4 pt-4 text-center">
        <p className="text-xs text-gray-500">شكراً لتعاملكم معنا</p>
        <p className="text-xs text-gray-400 mt-1">Powered by Nazario-Hesabaty</p>
      </div>
    </div>
  );
});

// محتوى وصل التالفات
const DamageContent = ({ data }) => (
  <div className="space-y-3">
    <div className="bg-red-50 p-3 rounded-lg">
      <h3 className="font-bold text-red-700 mb-2">تفاصيل التالف</h3>
      <table className="w-full text-sm">
        <tbody>
          <tr>
            <td className="text-gray-600 py-1">المنتج:</td>
            <td className="font-semibold">{data.product_name}</td>
          </tr>
          <tr>
            <td className="text-gray-600 py-1">الكمية:</td>
            <td className="font-semibold">{data.quantity}</td>
          </tr>
          <tr>
            <td className="text-gray-600 py-1">سعر الوحدة:</td>
            <td className="font-semibold">{formatCurrency(data.unit_price)}</td>
          </tr>
          <tr>
            <td className="text-gray-600 py-1">السبب:</td>
            <td className="font-semibold">{data.reason_text || data.reason}</td>
          </tr>
          {data.custom_reason && (
            <tr>
              <td className="text-gray-600 py-1">ملاحظات:</td>
              <td className="font-semibold">{data.custom_reason}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    <div className="bg-gray-100 p-3 rounded-lg text-center">
      <span className="text-gray-600">إجمالي الخسارة:</span>
      <p className="text-2xl font-bold text-red-600">{formatCurrency(data.total_loss)}</p>
    </div>
  </div>
);

// محتوى وصل الراتب
const SalaryContent = ({ data, shopInfo }) => (
  <div className="space-y-3">
    <div className="bg-blue-50 p-3 rounded-lg">
      <h3 className="font-bold text-blue-700 mb-2">بيانات الموظف</h3>
      <table className="w-full text-sm">
        <tbody>
          <tr>
            <td className="text-gray-600 py-1">الاسم:</td>
            <td className="font-semibold">{data.employee_name}</td>
          </tr>
          <tr>
            <td className="text-gray-600 py-1">المنصب:</td>
            <td className="font-semibold">{data.position || "موظف"}</td>
          </tr>
          <tr>
            <td className="text-gray-600 py-1">الشهر:</td>
            <td className="font-semibold">{data.month || "---"}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div className="bg-gray-100 p-3 rounded-lg">
      <h3 className="font-bold mb-2">تفاصيل الراتب</h3>
      <table className="w-full text-sm">
        <tbody>
          <tr>
            <td className="text-gray-600 py-1">الراتب الأساسي:</td>
            <td className="font-semibold">{formatCurrency(data.base_salary)}</td>
          </tr>
          {data.bonus > 0 && (
            <tr>
              <td className="text-gray-600 py-1">المكافآت:</td>
              <td className="font-semibold text-green-600">+{formatCurrency(data.bonus)}</td>
            </tr>
          )}
          {data.deductions > 0 && (
            <tr>
              <td className="text-gray-600 py-1">الخصومات:</td>
              <td className="font-semibold text-red-600">-{formatCurrency(data.deductions)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    <div className="bg-emerald-100 p-3 rounded-lg text-center">
      <span className="text-gray-600">صافي الراتب:</span>
      <p className="text-2xl font-bold text-emerald-700">{formatCurrency(data.net_salary || data.amount)}</p>
    </div>
    {/* توقيع */}
    <div className="flex justify-between mt-4 pt-4 border-t">
      <div className="text-center">
        <p className="text-xs text-gray-500 mb-8">توقيع الموظف</p>
        <div className="border-t border-gray-400 w-32"></div>
      </div>
      <div className="text-center">
        <p className="text-xs text-gray-500 mb-8">توقيع المسؤول</p>
        <div className="border-t border-gray-400 w-32"></div>
      </div>
    </div>
  </div>
);

// محتوى وصل التقسيط
const InstallmentContent = ({ data }) => (
  <div className="space-y-3">
    <div className="bg-purple-50 p-3 rounded-lg">
      <h3 className="font-bold text-purple-700 mb-2">بيانات العميل</h3>
      <table className="w-full text-sm">
        <tbody>
          <tr>
            <td className="text-gray-600 py-1">الاسم:</td>
            <td className="font-semibold">{data.customer_name}</td>
          </tr>
          {data.customer_phone && (
            <tr>
              <td className="text-gray-600 py-1">الهاتف:</td>
              <td className="font-semibold">{data.customer_phone}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    <div className="bg-gray-100 p-3 rounded-lg">
      <h3 className="font-bold mb-2">تفاصيل الدفعة</h3>
      <table className="w-full text-sm">
        <tbody>
          <tr>
            <td className="text-gray-600 py-1">رقم القسط:</td>
            <td className="font-semibold">{data.installment_number} من {data.total_installments}</td>
          </tr>
          <tr>
            <td className="text-gray-600 py-1">تاريخ الدفع:</td>
            <td className="font-semibold">{formatDateTime(data.payment_date).split(" ")[0]}</td>
          </tr>
          <tr>
            <td className="text-gray-600 py-1">القسط التالي:</td>
            <td className="font-semibold">{data.next_due_date ? formatDateTime(data.next_due_date).split(" ")[0] : "---"}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div className="bg-emerald-100 p-3 rounded-lg text-center">
      <span className="text-gray-600">المبلغ المدفوع:</span>
      <p className="text-2xl font-bold text-emerald-700">{formatCurrency(data.amount)}</p>
    </div>
    <div className="bg-amber-50 p-3 rounded-lg">
      <div className="flex justify-between text-sm">
        <span className="text-gray-600">إجمالي المبلغ:</span>
        <span className="font-semibold">{formatCurrency(data.total_amount)}</span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="text-gray-600">المدفوع:</span>
        <span className="font-semibold text-emerald-600">{formatCurrency(data.paid_amount)}</span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="text-gray-600">المتبقي:</span>
        <span className="font-semibold text-red-600">{formatCurrency(data.remaining_amount)}</span>
      </div>
    </div>
  </div>
);

// محتوى تقرير إغلاق الصندوق
const CashboxContent = ({ data }) => (
  <div className="space-y-3">
    <div className="bg-emerald-50 p-3 rounded-lg">
      <h3 className="font-bold text-emerald-700 mb-2">ملخص اليوم</h3>
      <table className="w-full text-sm">
        <tbody>
          <tr>
            <td className="text-gray-600 py-1">الرصيد الافتتاحي:</td>
            <td className="font-semibold">{formatCurrency(data.opening_balance)}</td>
          </tr>
          <tr>
            <td className="text-gray-600 py-1">إجمالي المبيعات:</td>
            <td className="font-semibold text-emerald-600">+{formatCurrency(data.total_sales)}</td>
          </tr>
          <tr>
            <td className="text-gray-600 py-1">المقبوضات:</td>
            <td className="font-semibold text-emerald-600">+{formatCurrency(data.cash_in)}</td>
          </tr>
          <tr>
            <td className="text-gray-600 py-1">المصروفات:</td>
            <td className="font-semibold text-red-600">-{formatCurrency(data.cash_out)}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div className="bg-gray-100 p-3 rounded-lg">
      <h3 className="font-bold mb-2">تفاصيل المبيعات</h3>
      <table className="w-full text-sm">
        <tbody>
          <tr>
            <td className="text-gray-600 py-1">عدد الفواتير:</td>
            <td className="font-semibold">{data.invoices_count || 0}</td>
          </tr>
          <tr>
            <td className="text-gray-600 py-1">مبيعات نقدية:</td>
            <td className="font-semibold">{formatCurrency(data.cash_sales)}</td>
          </tr>
          <tr>
            <td className="text-gray-600 py-1">مبيعات آجلة:</td>
            <td className="font-semibold">{formatCurrency(data.credit_sales)}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div className="bg-blue-100 p-3 rounded-lg text-center">
      <span className="text-gray-600">الرصيد المتوقع:</span>
      <p className="text-xl font-bold text-blue-700">{formatCurrency(data.expected_balance)}</p>
    </div>
    <div className="bg-emerald-100 p-3 rounded-lg text-center">
      <span className="text-gray-600">الرصيد الفعلي:</span>
      <p className="text-2xl font-bold text-emerald-700">{formatCurrency(data.actual_balance)}</p>
    </div>
    {data.difference !== 0 && (
      <div className={`p-3 rounded-lg text-center ${data.difference > 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
        <span className="text-gray-600">الفرق:</span>
        <p className={`text-lg font-bold ${data.difference > 0 ? 'text-emerald-700' : 'text-red-700'}`}>
          {data.difference > 0 ? '+' : ''}{formatCurrency(data.difference)}
        </p>
      </div>
    )}
    {/* توقيع */}
    <div className="text-center mt-4 pt-4 border-t">
      <p className="text-xs text-gray-500 mb-8">توقيع المسؤول</p>
      <div className="border-t border-gray-400 w-40 mx-auto"></div>
    </div>
  </div>
);

// دالة لإنشاء نص الوصل للواتساب
export const generateReceiptText = (type, data, shopInfo) => {
  let text = `*${shopInfo.name || "اسم المؤسسة"}*\n`;
  text += `${"═".repeat(20)}\n\n`;
  
  switch (type) {
    case "damage":
      text += `📋 *وصل تالفات*\n\n`;
      text += `📦 المنتج: ${data.product_name}\n`;
      text += `🔢 الكمية: ${data.quantity}\n`;
      text += `💰 سعر الوحدة: ${formatCurrency(data.unit_price)}\n`;
      text += `📝 السبب: ${data.reason_text || data.reason}\n`;
      if (data.custom_reason) text += `📌 ملاحظات: ${data.custom_reason}\n`;
      text += `\n💸 *إجمالي الخسارة: ${formatCurrency(data.total_loss)}*\n`;
      break;
      
    case "salary":
      text += `💼 *وصل راتب*\n\n`;
      text += `👤 الموظف: ${data.employee_name}\n`;
      text += `📋 المنصب: ${data.position || "موظف"}\n`;
      text += `📅 الشهر: ${data.month || "---"}\n\n`;
      text += `💵 الراتب الأساسي: ${formatCurrency(data.base_salary)}\n`;
      if (data.bonus > 0) text += `➕ المكافآت: ${formatCurrency(data.bonus)}\n`;
      if (data.deductions > 0) text += `➖ الخصومات: ${formatCurrency(data.deductions)}\n`;
      text += `\n💰 *صافي الراتب: ${formatCurrency(data.net_salary || data.amount)}*\n`;
      break;
      
    case "installment":
      text += `📅 *وصل دفعة تقسيط*\n\n`;
      text += `👤 العميل: ${data.customer_name}\n`;
      if (data.customer_phone) text += `📞 الهاتف: ${data.customer_phone}\n`;
      text += `\n🔢 رقم القسط: ${data.installment_number} من ${data.total_installments}\n`;
      text += `💰 المبلغ المدفوع: ${formatCurrency(data.amount)}\n`;
      text += `📆 تاريخ الدفع: ${formatDateTime(data.payment_date).split(" ")[0]}\n`;
      if (data.next_due_date) text += `⏭️ القسط التالي: ${formatDateTime(data.next_due_date).split(" ")[0]}\n`;
      text += `\n📊 إجمالي: ${formatCurrency(data.total_amount)} | مدفوع: ${formatCurrency(data.paid_amount)} | متبقي: ${formatCurrency(data.remaining_amount)}\n`;
      break;
      
    case "cashbox":
      text += `🏦 *تقرير إغلاق الصندوق*\n\n`;
      text += `📅 التاريخ: ${formatDateTime(data.date || new Date()).split(" ")[0]}\n\n`;
      text += `💵 الرصيد الافتتاحي: ${formatCurrency(data.opening_balance)}\n`;
      text += `📈 إجمالي المبيعات: ${formatCurrency(data.total_sales)}\n`;
      text += `➕ المقبوضات: ${formatCurrency(data.cash_in)}\n`;
      text += `➖ المصروفات: ${formatCurrency(data.cash_out)}\n`;
      text += `\n🔢 عدد الفواتير: ${data.invoices_count || 0}\n`;
      text += `💵 مبيعات نقدية: ${formatCurrency(data.cash_sales)}\n`;
      text += `📝 مبيعات آجلة: ${formatCurrency(data.credit_sales)}\n`;
      text += `\n📊 الرصيد المتوقع: ${formatCurrency(data.expected_balance)}\n`;
      text += `💰 *الرصيد الفعلي: ${formatCurrency(data.actual_balance)}*\n`;
      if (data.difference !== 0) {
        text += `${data.difference > 0 ? '✅' : '⚠️'} الفرق: ${data.difference > 0 ? '+' : ''}${formatCurrency(data.difference)}\n`;
      }
      break;
  }
  
  text += `\n${"═".repeat(20)}\n`;
  text += `_Powered by Nazario-Hesabaty_`;
  
  return text;
};

// دالة للمشاركة عبر واتساب
export const shareViaWhatsApp = (type, data, shopInfo, phoneNumber = "") => {
  const text = generateReceiptText(type, data, shopInfo);
  const encodedText = encodeURIComponent(text);
  const url = phoneNumber 
    ? `https://wa.me/${phoneNumber}?text=${encodedText}`
    : `https://wa.me/?text=${encodedText}`;
  window.open(url, "_blank");
};

export default ProfessionalReceipt;
